import axios from "axios";

import {
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";

export const UserContext = createContext({});

export function UserContextProvider({
  children,
}) {
  const [user, setUserState] =
    useState(null);

  const [isLoading, setLoading] =
    useState(true);

  const [updateFlag, setUpdateFlag] =
    useState(false);

  const [userEmail, setUserEmail] =
    useState("");

  const [
    userFirstName,
    setUserFirstName,
  ] = useState("");

  const [
    userLastName,
    setUserLastName,
  ] = useState("");

  /*
   * Licenses should always be an array.
   *
   * This prevents components from having to check whether
   * userLicenses is a string, null, or an array.
   */
  const [
    userLicenses,
    setUserLicenses,
  ] = useState([]);

  const [
    licensesLoading,
    setLicensesLoading,
  ] = useState(false);

  /*
   * This is important for product cards and purchase buttons.
   *
   * An empty license array before the API request completes does
   * not necessarily mean the user owns nothing.
   */
  const [
    licensesLoaded,
    setLicensesLoaded,
  ] = useState(false);

  const [
    userShoppingCart,
    setUserShoppingCart,
  ] = useState([]);

  /*
   * Retrieve all license records belonging to the logged-in user.
   *
   * This does not retrieve product information.
   */
  const refreshLicenses = useCallback(
    async (providedToken) => {
      const token =
        providedToken ||
        localStorage.getItem("token");

      if (!token) {
        setUserLicenses([]);
        setLicensesLoading(false);
        setLicensesLoaded(true);

        return [];
      }

      try {
        setLicensesLoading(true);
        setLicensesLoaded(false);

        const { data } = await axios.post(
          "/api/get-licenses",
          {
            token,
          }
        );

        if (!data || data.error) {
          throw new Error(
            data?.error ||
              "Unable to load licenses."
          );
        }

        const licenses = Array.isArray(
          data.licenses
        )
          ? data.licenses
          : [];

        setUserLicenses(licenses);
        setLicensesLoaded(true);
        return licenses;
      } catch (error) {
        /*
         * Do not clear the previous license data on a temporary
         * network or server error. Also leave licensesLoaded false
         * so the store does not mistakenly assume the user owns
         * nothing.
         */
        setLicensesLoaded(false);

        console.error(
          "Unable to retrieve licenses:",
          error
        );

        throw error;
      } finally {
        setLicensesLoading(false);
      }
    },
    []
  );

  /*
   * Apply profile information to the context.
   *
   * License retrieval remains separate so it can also be refreshed
   * after purchases, redemptions, and subscription changes.
   */
  const applyUser = useCallback(
    (userData) => {
      const normalizedUser =
        userData || null;

      setUserState(normalizedUser);

      if (normalizedUser) {
        setUserEmail(
          normalizedUser.email || ""
        );

        setUserFirstName(
          normalizedUser.firstName || ""
        );

        setUserLastName(
          normalizedUser.lastName || ""
        );

        setUserShoppingCart(
          Array.isArray(
            normalizedUser.shoppingCart
          )
            ? normalizedUser.shoppingCart
            : []
        );

        /*
         * A new user has been applied, but their licenses have not
         * necessarily finished loading yet.
         */
        setLicensesLoaded(false);
      } else {
        setUserEmail("");
        setUserFirstName("");
        setUserLastName("");
        setUserShoppingCart([]);
        setUserLicenses([]);
        setLicensesLoading(false);

        /*
         * For a logged-out visitor, an empty license list is the
         * complete and expected state.
         */
        setLicensesLoaded(true);
      }
    },
    []
  );

  /*
   * Keep this function for components that already call setUser().
   *
   * It applies the user immediately and then refreshes licenses.
   */
  const setUser = useCallback(
    (userData) => {
      applyUser(userData);

      if (userData) {
        refreshLicenses().catch(
          (error) => {
            console.error(
              "Unable to refresh licenses after setting user:",
              error
            );
          }
        );
      }
    },
    [
      applyUser,
      refreshLicenses,
    ]
  );

  /*
   * Fetch the authenticated profile and licenses.
   */
  const refreshUser = useCallback(
    async (providedToken) => {
      const token =
        providedToken ||
        localStorage.getItem("token");

      if (!token) {
        applyUser(null);
        setLoading(false);

        return null;
      }

      setLoading(true);

      try {
        const { data } =
          await axios.post(
            "/profile",
            {
              token,
            }
          );

        if (!data || data.error) {
          throw new Error(
            data?.error ||
              "Unable to load the user profile."
          );
        }

        applyUser(data);

        /*
         * A license request failure should not log the user out.
         * Their profile authentication may still be completely valid.
         */
        try {
          await refreshLicenses(token);
        } catch (licenseError) {
          console.error(
            "The user profile loaded, but licenses could not be loaded:",
            licenseError
          );
        }

        return data;
      } catch (error) {
        /*
         * This catch applies to the profile request itself. A failed
         * profile authentication means the stored token should be
         * removed.
         */
        localStorage.removeItem("token");
        applyUser(null);

        throw error;
      } finally {
        setLoading(false);
      }
    },
    [
      applyUser,
      refreshLicenses,
    ]
  );

  useEffect(() => {
    refreshUser().catch(() => {
      /*
       * refreshUser already removes invalid authentication data
       * and clears the context.
       */
    });
  }, [refreshUser]);

  const logout = async () => {
    try {
      await axios.get("/logout", {
        withCredentials: true,
      });
    } catch (error) {
      console.error(
        "Logout failed:",
        error
      );
    } finally {
      localStorage.removeItem("token");
      applyUser(null);
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        refreshUser,
        isLoading,
        logout,

        updateFlag,
        setUpdateFlag,

        userEmail,
        setUserEmail,

        userFirstName,
        setUserFirstName,

        userLastName,
        setUserLastName,

        userShoppingCart,
        setUserShoppingCart,

        userLicenses,
        setUserLicenses,
        refreshLicenses,
        licensesLoading,
        licensesLoaded,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

// import axios from "axios";
// import {
//   createContext,
//   useCallback,
//   useEffect,
//   useState,
// } from "react";

// export const UserContext = createContext({});

// export function UserContextProvider({ children }) {
//   const [user, setUserState] = useState(null);
//   const [isLoading, setLoading] = useState(true);

//   const [updateFlag, setUpdateFlag] = useState(false);
//   const [userEmail, setUserEmail] = useState("");
//   const [userFirstName, setUserFirstName] = useState("");
//   const [userLastName, setUserLastName] = useState("");
//   const [userLicenses, setUserLicenses] = useState("");
//   const [userShoppingCart, setUserShoppingCart] = useState([]);


//   const getLicenses = useCallback(async () => {
//     console.log("getting licenses...")
//     const token = localStorage.getItem("token");
//     if(token)
//     {
//         const { data } = await axios.post(
//             "/api/get_my_gear",
//             { token })

//         if(data)
//         {
//             console.log("success! setting licenses..")
//             setUserLicenses(data);
//             console.log("licenses:");
//             console.log(userLicenses);
//         }
//     }

//   },[]);

//   const applyUser = useCallback((userData) => {
//     setUserState(userData || null);
//     console.log("setting user");

//     if (userData) {
//       setUserEmail(userData.email || "");
//       setUserFirstName(userData.firstName || "");
//       setUserLastName(userData.lastName || "");
//       const licenses = getLicenses();
//       setUserShoppingCart(userData.shoppingCart || []);
//     } else {
//       setUserEmail("");
//       setUserFirstName("");
//       setUserLastName("");
//       setUserShoppingCart([]);
//     }
//   }, []);

//   /*
//    * Keep this function for components that already call setUser.
//    * It is intentionally synchronous; React state setters themselves
//    * cannot be awaited.
//    */
//   const setUser = useCallback(
//     (userData) => {
//       applyUser(userData);
//     },
//     [applyUser]
//   );

//   /*
//    * Fetch the authenticated user using the token, update the context,
//    * and return the fetched user data to the caller.
//    */
//   const refreshUser = useCallback(
//     async (providedToken) => {
//       const token =
//         providedToken || localStorage.getItem("token");

//       if (!token) {
//         applyUser(null);
//         setLoading(false);
//         return null;
//       }

//       setLoading(true);

//       try {
//         const { data } = await axios.post("/profile", {
//           token,
//         });

//         if (!data || data.error) {
//           throw new Error(
//             data?.error || "Unable to load the user profile."
//           );
//         }

//         applyUser(data);
//         return data;
//       } catch (error) {
//         localStorage.removeItem("token");
//         applyUser(null);
//         throw error;
//       } finally {
//         setLoading(false);
//       }
//     },
//     [applyUser]
//   );

//   useEffect(() => {
//     refreshUser().catch(() => {
//       // refreshUser already clears invalid authentication data.
//     });
//   }, [refreshUser]);

//   const logout = async () => {
//     try {
//       await axios.get("/logout", {
//         withCredentials: true,
//       });
//     } catch (error) {
//       console.error("Logout failed:", error);
//     } finally {
//       localStorage.removeItem("token");
//       applyUser(null);
//     }
//   };

//   return (
//     <UserContext.Provider
//       value={{
//         user,
//         setUser,
//         refreshUser,
//         isLoading,
//         logout,

//         updateFlag,
//         setUpdateFlag,

//         userEmail,
//         setUserEmail,

//         userFirstName,
//         setUserFirstName,

//         userLastName,
//         setUserLastName,

//         userShoppingCart,
//         setUserShoppingCart,

//         userLicenses,
//         setUserLicenses,
//       }}
//     >
//       {children}
//     </UserContext.Provider>
//   );
// }

// // import axios from 'axios';
// // import { createContext, useState, useEffect, useContext } from 'react';

// // export const UserContext = createContext({});

// // export function UserContextProvider({ children }) {
// //     const [user, setUserState] = useState(null);
// //     const [isLoading, setLoading] = useState(true);

// //     const [updateFlag, setUpdateFlag] = useState(false);
// //     const [userEmail, setUserEmail] = useState(false);
// //     const [userFirstName, setUserFirstName] = useState(false);
// //     const [userLastName, setUserLastName] = useState(false);
// //     const [userShoppingCart, setUserShoppingCart] = useState([]);
    

// //     const setUser = async (userData) => {    
        
// //         //console.log("Data has been sent to setUser:")
// //         //console.log(userData)
// //         setLoading(true)     
// //         setUserState(userData);
// //         if(userData)
// //         {
// //             setUserEmail(userData.email)
// //             setUserFirstName(userData.firstName)
// //             setUserLastName(userData.lastName)
// //             setUserShoppingCart(userData.shoppingCart)
// //         }
// //         else
// //         {
// //             setUserEmail("")
// //             setUserFirstName("")
// //             setUserLastName("")
// //             setUserShoppingCart("")
// //         }       

// //         return new Promise((resolve) => {

// //             resolve();
// //             setLoading(false);

// //         });
// //     };







// //     useEffect(() => {
        
// //         setLoading(true)
// //         //console.log("sending authorization, token")
// //         //console.log(localStorage.token)
// //         const userToken = localStorage.token


        
// //         if(userToken)
// //         {
// //             axios.post('/profile', {token: userToken}).then(({ data }) => {
// //             //console.log(data)
// //             setUser(data);

// //             setLoading(false);
// //         }).catch(() => {
// //             setLoading(false);
// //         });


// //         // try{
// //         //     axios.post('/profile',{token: userToken,}).then({res});
// //         //       console.log("response:")
// //         //       console.log(res)
// //         //       setLoading(false)
// //         //   }
// //         //   catch{
// //         //     console.log("catch fired..")
// //         //     setUser(null);
// //         //     setLoading(false)
// //         //   }




// //         }
// //         if(!userToken)
// //         {
// //             setLoading(false)
// //             //console.log("NO TOKEN FOUND")
// //         }


// //           //window.location.href = windowsDownloadURI;


        
// //         // axios.get('/profile').then(({ data }) => {
// //         //     console.log(data)
// //         //     //console.log("user data: ")
// //         //     //console.log(data);
// //         //     setUser(data);

// //         //     setLoading(false);
// //         // }).catch(() => {
// //         //     setLoading(false);
// //         // });

// //     }, []);

// //     const logout = async () => {
// //         try {
// //             const response = await axios.get('/logout', { withCredentials: true });
// //             //console.log(response.data.message);  // Log the server response message
// //             setUser(null);  // Clear user context
// //         } catch (error) {
// //             console.error('Logout failed:', error);
// //         }
// //     };

// //     return (
// //         <UserContext.Provider value={{ user, setUser, isLoading, logout, updateFlag, setUpdateFlag, userEmail, setUserEmail, userFirstName, setUserFirstName, userLastName, setUserLastName, userShoppingCart, setUserShoppingCart }}>
// //             {children}
// //         </UserContext.Provider>
// //     );
// // }

